import { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import companyBasedRequest from "../../request/companyBasedRequest";
import PhoneInput from "../common/phoneInput/phoneInput";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import CATEGORY_OPTIONS from "../../constants/categoryOptions";
import logo from "../../images/LogoFullText.png";
import { BellRing, CalendarClock, Gift, ShieldCheck, Sparkles, Zap } from "lucide-react";
import { BUSINESS_SIGNUP_ENABLED } from "../../constants/featureFlags";
import "./listShopPage.css";
import RequestCategoryModal from "./requestCategoryModal/requestCategoryModal";

const ListShopPage = () => {
	const { language } = useContext(CombinedContext);
	const { ref } = useParams();

	const [shopName, setShopName] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [category, setCategory] = useState("");
	const [subCategory, setSubCategory] = useState("");
	const [categoryOpen, setCategoryOpen] = useState(false);
	const [subCategoryOpen, setSubCategoryOpen] = useState(false);
	const [otherDescription, setOtherDescription] = useState("");
	const [loading, setLoading] = useState(false);
	const [banner, setBanner] = useState({ text: "", type: "info", exiting: false });
	const [manualRef, setManualRef] = useState(ref || "");
	const [categoryModalOpen, setCategoryModalOpen] = useState(false);
	const [invalidFields, setInvalidFields] = useState(new Set());

	/* Gated by constants/featureFlags. When sign-up is off the form below is
	   left exactly as it was, just switched off behind a notice. */
	const signupPaused = !BUSINESS_SIGNUP_ENABLED;

	useEffect(() => {
		if (!banner || !banner.text) return;
		const exitTimer = setTimeout(() => setBanner((prev) => ({ ...prev, exiting: true })), 3000);
		const removeTimer = setTimeout(() => setBanner({ text: "", type: "info", exiting: false }), 3250);
		return () => {
			clearTimeout(exitTimer);
			clearTimeout(removeTimer);
		};
	}, [banner]);

	/* The banner says what's wrong; these say where. A flagged field clears
	   as soon as it's touched, so the red only ever marks what's still open. */
	const clearInvalid = (field) => {
		setInvalidFields((prev) => {
			if (!prev.has(field)) return prev;
			const next = new Set(prev);
			next.delete(field);
			return next;
		});
	};

	const invalidClass = (field) => (invalidFields.has(field) ? " is-invalid" : "");

	const handleSubmit = async () => {
    if (signupPaused) return;

    const missing = new Set();
    if (!shopName) missing.add("shopName");
    if (!category) missing.add("category");
    if (!subCategory) missing.add("subCategory");
    if (!firstName) missing.add("firstName");
    if (!lastName) missing.add("lastName");
    if (!email) missing.add("email");
    if (!phone) missing.add("phone");

    if (missing.size > 0) {
        setInvalidFields(missing);
        setBanner({
            type: "error",
            text: language === "es" ? "Por favor completa todos los campos." : "Please fill in all required fields.",
        });
        return;
    }

    setInvalidFields(new Set());
    setBanner(null);
    setLoading(true);

    try {
        const calls = [
            companyBasedRequest.requestShopInvite({
                shopName,
                firstName,
                lastName,
                email,
                phone,
                category,
                subCategory,
                referralCode: manualRef || null,
            }),
        ];

        if (otherDescription) {
            calls.push(companyBasedRequest.requestNewCategory({
                description: otherDescription,
				email: email 
            }));
        }

        const [response] = await Promise.all(calls);

        const msg =
            response?.message?.[language] ||
            (language === "es" ? "¡Gracias! Revisa tu correo electrónico para conocer los próximos pasos." : "Thanks! Check your email for the next steps.");

        setBanner({ type: "success", text: msg });
        setShopName("");
        setFirstName("");
        setLastName("");
        setEmail("");
        setPhone("");
        setCategory("");
        setSubCategory("");
        setOtherDescription("");
    } catch (err) {
        console.error("ListShop error:", err);
        const msg =
            err?.response?.data?.error?.[language] ||
            err?.response?.data?.message?.[language] ||
            err.message ||
            (language === "es" ? "No pudimos enviar tu solicitud. Inténtalo de nuevo." : "We couldn't send your request. Please try again.");
        setBanner({ type: "error", text: msg });
    } finally {
        setLoading(false);
    }
};

	const selectedCategory = CATEGORY_OPTIONS.find((c) => c.value === category);
	const availableSubCategories = selectedCategory?.subCategories || [];

	const t = translations[language].listShop;
	const perks = [
		{ icon: <Sparkles size={19} strokeWidth={2} />, title: t.perkFreeTitle, desc: t.perkFreeDesc },
		{ icon: <Zap size={19} strokeWidth={2} />, title: t.perk1Title, desc: t.perk1Desc },
		{ icon: <BellRing size={19} strokeWidth={2} />, title: t.perk2Title, desc: t.perk2Desc },
		{ icon: <Gift size={19} strokeWidth={2} />, title: t.perk3Title, desc: t.perk3Desc },
	];

	return (
		<div className="sf-auth-page sf-ls-root">
			<div className="sf-ls-split">
				{/* ── LEFT PANEL — why bother ── */}
				<div className="sf-ls-left">
					{/* <img src={logo} alt="Sin Fila" className="sf-ls-logo" /> */}

					<div className="sf-ls-left-body">
						<h2 className="sf-ls-left-title">{t.leftTitle}</h2>

						<div className="sf-ls-perks">
							{perks.map((perk) => (
								<div className="sf-ls-perk" key={perk.title}>
									<span className="sf-ls-perk-icon">{perk.icon}</span>
									<span className="sf-ls-perk-text">
										<strong>{perk.title}</strong>
										<span>{perk.desc}</span>
									</span>
								</div>
							))}
						</div>
					</div>

					<p className="sf-ls-trust">
						<ShieldCheck size={15} strokeWidth={2} />
						{t.trustNote}
					</p>
				</div>

				{/* ── RIGHT PANEL — FORM ── */}
				<div className="sf-ls-right sf-auth-form">
					<div className="sf-ls-form-head">
						<h1 className="sf-ls-form-title">{t.title}</h1>
						<p className="sf-ls-form-sub">{t.subtitle}</p>
					</div>

					{signupPaused && (
						<div className="sf-ls-notice" role="status">
							<CalendarClock size={19} strokeWidth={2} />
							<span className="sf-ls-notice-text">
								<strong className="sf-ls-notice-title">{t.pausedTitle}</strong>
								<span className="sf-ls-notice-body">{t.pausedBody}</span>
							</span>
						</div>
					)}

					{/* One fieldset switches every control below off at once, so the
					    form can stay intact while sign-up is paused. */}
					<fieldset className="sf-ls-form-body" disabled={signupPaused}>
					<p className="sf-auth-legend">{t.sectionBusiness}</p>

					{/* Shop name */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.shopName}</label>
						<input
							className={`sf-auth-input${invalidClass("shopName")}`}
							placeholder={t.shopName}
							value={shopName}
							onChange={(e) => {
								setShopName(e.target.value);
								clearInvalid("shopName");
							}}
						/>
					</div>

					{/* Category row */}
					<div className="sf-auth-row2">
						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.category}</label>
							<div className="sf-auth-select">
								<button
									type="button"
									className={`sf-auth-select-btn${category ? " is-selected" : ""}${categoryOpen ? " is-open" : ""}${invalidClass("category")}`}
									onClick={() => {
										setCategoryOpen((p) => !p);
										setSubCategoryOpen(false);
									}}
								>
									<span>
										{category ? (language === "es" ? selectedCategory.labelES : selectedCategory.labelEN) : t.categoryPlaceholder}
									</span>
									<span className={`sf-auth-caret${categoryOpen ? " is-open" : ""}`}>▼</span>
								</button>
								{categoryOpen && (
									<ul className="sf-auth-select-list">
										{CATEGORY_OPTIONS.map((c) => (
											<li
												key={c.value}
												className={`sf-auth-select-item${category === c.value ? " is-active" : ""}`}
												onClick={() => {
													setCategory(c.value);
													setSubCategory("");
													setCategoryOpen(false);
													clearInvalid("category");
												}}
											>
												{language === "es" ? c.labelES : c.labelEN}
											</li>
										))}
									</ul>
								)}
							</div>
						</div>

						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.subCategory}</label>
							<div className="sf-auth-select">
								<button
									type="button"
									className={`sf-auth-select-btn${subCategory ? " is-selected" : ""}${subCategoryOpen ? " is-open" : ""}${invalidClass("subCategory")}`}
									disabled={!category}
									onClick={() => setSubCategoryOpen((p) => !p)}
								>
									<span>
										{subCategory
											? language === "es"
												? availableSubCategories.find((s) => s.value === subCategory)?.labelES
												: availableSubCategories.find((s) => s.value === subCategory)?.labelEN
											: t.subCategoryPlaceholder}
									</span>
									<span className={`sf-auth-caret${subCategoryOpen ? " is-open" : ""}`}>▼</span>
								</button>
								{subCategoryOpen && (
									<ul className="sf-auth-select-list">
										{availableSubCategories.map((sc) => (
											<li
												key={sc.value}
												className={`sf-auth-select-item${subCategory === sc.value ? " is-active" : ""}`}
												onClick={() => {
													setSubCategory(sc.value);
													setSubCategoryOpen(false);
													clearInvalid("subCategory");
												}}
											>
												{language === "es" ? sc.labelES : sc.labelEN}
											</li>
										))}
									</ul>
								)}
							</div>
						</div>
					</div>

					<button type="button" className="sf-ls-request-cat" onClick={() => setCategoryModalOpen(true)}>
						{t.requestCategory}
					</button>

					<p className="sf-auth-legend">{t.sectionContact}</p>

					{/* Name row */}
					<div className="sf-auth-row2">
						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.firstName}</label>
							<input
								className={`sf-auth-input${invalidClass("firstName")}`}
								placeholder={t.firstName}
								value={firstName}
								onChange={(e) => {
									setFirstName(e.target.value);
									clearInvalid("firstName");
								}}
								autoComplete="given-name"
							/>
						</div>
						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.lastName}</label>
							<input
								className={`sf-auth-input${invalidClass("lastName")}`}
								placeholder={t.lastName}
								value={lastName}
								onChange={(e) => {
									setLastName(e.target.value);
									clearInvalid("lastName");
								}}
								autoComplete="family-name"
							/>
						</div>
					</div>

					{/* Email */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.email}</label>
						<input
							className={`sf-auth-input${invalidClass("email")}`}
							type="email"
							placeholder={t.email}
							value={email}
							onChange={(e) => {
								setEmail(e.target.value);
								clearInvalid("email");
							}}
							autoComplete="email"
						/>
					</div>

					{/* Phone */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.phone}</label>
						<PhoneInput
							businessPhone={phone}
							setBusinessPhone={(value) => {
								setPhone(value);
								clearInvalid("phone");
							}}
							className={invalidFields.has("phone") ? "is-invalid" : ""}
						/>
					</div>

					{/* Referral */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.referralCode}</label>
						<input
							className="sf-auth-input"
							placeholder={t.referralCodePlaceholder}
							value={manualRef}
							onChange={(e) => setManualRef(e.target.value.toUpperCase())}
						/>
					</div>

					<button className="sf-auth-cta" onClick={handleSubmit} disabled={loading}>
						{loading && <span className="sf-auth-spinner" aria-hidden="true" />}
						{loading ? t.sending || "Sending..." : t.registerShop}
					</button>
					</fieldset>

					<p className="sf-auth-foot">
						{t.alreadyHaveAccount} <Link to="/signin">{t.signIn}</Link>
					</p>
				</div>
			</div>

			{banner && <BannerMessageFixed type={banner.type} text={banner.text} />}
			<RequestCategoryModal
				open={categoryModalOpen}
				onClose={() => setCategoryModalOpen(false)}
				onSuggestionSubmit={(suggestion) => {
					setOtherDescription(suggestion);
					setBanner({
						type: "success",
						text: language === "es" ? "Categoría guardada. Completa el formulario y envía." : "Category saved. Complete the form and submit.",
						exiting: false,
					});
				}}
			/>
		</div>
	);
};

export default ListShopPage;
